import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose, isFunction, debounce } from 'underscore';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { ContextualMenu, DirectionalHint, SearchBox, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import { getStyles } from './styles';

const onRenderMenuHeader = columns => (
  <Stack
    horizontal
    className="menu-header"
    tabIndex={-1}
    role="menuitem"
  >
    {columns && columns.map(column => (
      <Stack.Item
        key={`col-header-${column.key}`}
        className={`col-header-${column.key}`}
        styles={{
          root: {
            minWidth: column.minWidth,
            maxWidth: column.maxWidth,
          },
        }}
      >{column.name}
      </Stack.Item>
    ))}
  </Stack>
);

const onRenderMenuItem = (dismissMenu, item, columns, onSelectItem) => (
  <Stack
    onClick={() => {
      dismissMenu();
      onSelectItem(item);
    }}
    horizontal
    className="menu-item"
    role="menuitem"
  >
    {columns && columns.map(column => (
      <Stack.Item
        key={`col-${column.key}`}
        className={`col-${column.key}`}
        styles={{
          root: {
            minWidth: column.minWidth,
            maxWidth: column.maxWidth,
          },
        }}
      >
        {column.onRender ? column.onRender(item) : item[column.key]}
      </Stack.Item>
    ))}
  </Stack>
);

const onRenderMenuLoading = i18n => (
  <Stack
    className="menu-loading"
    role="menuitem"
  >
    <Stack.Item><Spinner size={SpinnerSize.large} /></Stack.Item>
    <Stack.Item><span>{i18n.getString(_TL_('Loading...'))}</span></Stack.Item>
  </Stack>
);

const onRenderMenuEmpty = (dismissMenu, i18n) => (
  <Stack
    onClick={() => {
      dismissMenu();
    }}
    horizontal
    className="menu-empty"
    role="menuitem"
  >

    <Stack.Item>
      <span>{i18n.getString(_TL_('No results found'))}</span>
    </Stack.Item>
  </Stack>
);

function UnstyledDropdownSearchBox({
  i18n, classes, className, menuClassName, placeholder, searchText, onSearchTextChange, onSearchValidation, onSearch, columns, onSelectItem, items, isLoading, ariaLabel,
}) {
  const linkRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const onShowMenu = () => setShowMenu(true);
  const onHideMenu = () => setShowMenu(false);
  const [menuItems, setMenuItems] = useState([]);

  const onSearchWithValidation = useCallback(debounce((newSearchText) => {
    if (onSearchValidation && isFunction(onSearchValidation)) {
      const isValid = onSearchValidation(newSearchText);
      if (!isValid) {
        return;
      }
    }
    onShowMenu();
    onSearch(newSearchText);
  }, 500, false), [onSearch, onSearchValidation]);

  useEffect(() => {
    if (isLoading) {
      setMenuItems([{
        key: 'loading',
        onRender: () => onRenderMenuLoading(i18n),
      }]);
      return;
    }

    const newMenuHeader = [{
      key: 'menu-header',
      onRender: () => onRenderMenuHeader(columns),
    }];

    // render empty cases
    if (!items || items.length === 0) {
      setMenuItems(newMenuHeader.concat({
        key: 'menu-empty',
        onRender: (_item, dismissMenu) => onRenderMenuEmpty(dismissMenu, i18n),
      }));
      return;
    }

    // render non-empty case
    const newMenuItems = items.map((newItem, index) => ({
      key: `menu-item-${index}`,
      onRender: (_item, dismissMenu) => onRenderMenuItem(dismissMenu, newItem, columns, onSelectItem),
    }));
    setMenuItems(newMenuHeader.concat(newMenuItems));
  }, [columns, i18n, isLoading, items, onSelectItem]);

  return (
    <div className={classNames(classes.dropdownSearchContainer, 'dropdown-searchbox-container', className)}>
      <SearchBox
        ref={linkRef}
        placeholder={placeholder}
        onSearch={onSearchWithValidation}
        value={searchText}
        onClear={() => {
          onSearchTextChange('');
        }}
        onChange={(_, newValue) => {
          const text = newValue || '';
          onSearchTextChange(text);
          onSearchWithValidation(text);
        }}
        className="dropdown-searchbox"
        ariaLabel={ariaLabel || i18n.getString(_TL_('Search box'))}
      />
      <ContextualMenu
        target={linkRef}
        items={menuItems}
        hidden={!showMenu}
        onDismiss={onHideMenu}
        directionalHint={DirectionalHint.bottomLeftEdge}
        className={classNames(classes.dropdownSearchBoxMenu, 'dropdown-searchbox-menu', menuClassName)}
      />
    </div>
  );
}

UnstyledDropdownSearchBox.defaultProps = {
  className: '',
  menuClassName: '',
  placeholder: '',
  ariaLabel: '',
  onSearchValidation: null,
  isLoading: false,
};

UnstyledDropdownSearchBox.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    dropdownSearchContainer: PropTypes.string,
    dropdownSearchBoxMenu: PropTypes.string,
  }).isRequired,
  searchText: PropTypes.string.isRequired,
  onSearchTextChange: PropTypes.func.isRequired,
  onSearchValidation: PropTypes.func,
  onSearch: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onRender: PropTypes.func,
  })).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLoading: PropTypes.bool,
  onSelectItem: PropTypes.func.isRequired,
  className: PropTypes.string,
  menuClassName: PropTypes.string,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export const DropdownSearchBox = compose(
  withI18nConsumer,
  WrappedComponent => withDefaultStyles(WrappedComponent, getStyles)
)(UnstyledDropdownSearchBox);
